import { Box, Typography } from '@mui/material'
import { Fragment } from 'react'

import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import type { PrescriptionData } from '@/shared/types/yakurekiPrescription'

// 「前回からの差分」 = 3 col grid (= kind / 薬剤名 / 詳細).
// 薬剤名 col を独立させて 詳細の開始位置を全 row で揃える (= 薬剤名長依存ガタつきを排除).
//
//   新規   薬剤名         1錠 1日1回朝食後・30日
//   変更   薬剤名         2錠 → 3錠 / 朝夕食後 → 毎食後・30日
//   中止   薬剤名

// 補足の段 (= MetaText と同じ 12/400/fg.muted/1.5). caption variant 経由で文字拡大に追従する.
const SUPPLEMENT_SX = {
  fontWeight: 400,
  color: colorTokens.fg.muted,
  lineHeight: 1.5,
}

type Kind = 'added' | 'changed' | 'continued' | 'stopped'

type DiffRow = {
  key: string
  kind: Kind
  drug_name: string
  detail: string
}

type FlatDrug = {
  drug_name: string
  quantity: string
  unit: string
  yj_code?: string
  usage?: string
  days?: number
  key: string
}

interface Props {
  current?: PrescriptionData | null
  previous?: PrescriptionData | null
}

// 行頭 1-kanji label. Notion 流密度のため「新規」「変更」「中止」「継続」 を 1 文字に短縮:
//   新 / 変 / 中 / 継 (= 中止は行 text に line-through 併用).
// 印は補足の段 (12/400/fg.muted) の 1 色. 種類は字で分かり、 中止は取り消し線で分かるので
// 色を分けない (hub design-book ADR-0061).
const KIND_SHORT: Record<Kind, string> = {
  added: '新',
  changed: '変',
  continued: '継',
  stopped: '中',
}

const KIND_ORDER: Record<Kind, number> = {
  added: 0,
  changed: 1,
  continued: 2,
  stopped: 3,
}

const collectMedications = (
  rx: PrescriptionData | null | undefined
): FlatDrug[] => {
  if (!rx?.prescription_groups) return []
  return rx.prescription_groups.flatMap((group, gIdx) =>
    (group.drugs ?? []).map((drug, dIdx) => ({
      drug_name: drug.drug_name,
      quantity: drug.quantity,
      unit: drug.unit,
      yj_code: drug.yj_code,
      usage: group.usage?.name,
      days: group.days,
      key: `${gIdx}-${dIdx}`,
    }))
  )
}

const sameDrug = (a: FlatDrug, b: FlatDrug) =>
  (!!a.yj_code && a.yj_code === b.yj_code) ||
  (!a.yj_code && !b.yj_code && a.drug_name === b.drug_name)

const formatQuantity = (q: string, u: string): string => {
  if (!q) return ''
  return u ? `${q}${u}` : q
}

const formatUsageWithDays = (
  usage: string | undefined,
  days: number | undefined
): string => {
  if (usage && days != null) return `${usage}・${days}日`
  if (usage) return usage
  if (days != null) return `${days}日`
  return ''
}

const buildAddedDetail = (qty: string, usage: string): string =>
  [qty, usage].filter(Boolean).join('  ')

const buildChangedDetail = (
  prev_qty: string,
  curr_qty: string,
  prev_usage: string,
  curr_usage: string
): string => {
  const qtyChanged = curr_qty !== prev_qty
  const usageChanged = curr_usage !== prev_usage
  // 両方変更: 改行で 2 行に分ける (= "/" 区切り 1 行で wrap するより視認性 high).
  // pre-line で render するため "\n" join.
  if (qtyChanged && usageChanged) {
    return `${prev_qty} → ${curr_qty}\n${prev_usage} → ${curr_usage}`
  }
  if (qtyChanged) {
    return curr_usage
      ? `${prev_qty} → ${curr_qty} (${curr_usage})`
      : `${prev_qty} → ${curr_qty}`
  }
  return curr_qty
    ? `${prev_usage} → ${curr_usage} (${curr_qty})`
    : `${prev_usage} → ${curr_usage}`
}

export const PrescriptionWithDiffPanel = ({ current, previous }: Props) => {
  const currMeds = collectMedications(current)
  const prevMeds = collectMedications(previous)
  const hasPrev = prevMeds.length > 0

  if (currMeds.length === 0 && !hasPrev) {
    return (
      <Typography variant="body2" color="text.disabled" sx={{ py: 0.5 }}>
        処方情報なし
      </Typography>
    )
  }

  const rows: DiffRow[] = []

  if (!hasPrev) {
    for (const m of currMeds) {
      rows.push({
        key: `add-${m.key}`,
        kind: 'added',
        drug_name: m.drug_name,
        detail: buildAddedDetail(
          formatQuantity(m.quantity, m.unit),
          formatUsageWithDays(m.usage, m.days)
        ),
      })
    }
  } else {
    for (const med of currMeds) {
      const prev = prevMeds.find((p) => sameDrug(p, med))
      const curr_qty = formatQuantity(med.quantity, med.unit)
      const curr_usage = formatUsageWithDays(med.usage, med.days)
      if (!prev) {
        rows.push({
          key: `add-${med.key}`,
          kind: 'added',
          drug_name: med.drug_name,
          detail: buildAddedDetail(curr_qty, curr_usage),
        })
        continue
      }
      const prev_qty = formatQuantity(prev.quantity, prev.unit)
      const prev_usage = formatUsageWithDays(prev.usage, prev.days)
      if (curr_qty !== prev_qty || curr_usage !== prev_usage) {
        rows.push({
          key: `chg-${med.key}`,
          kind: 'changed',
          drug_name: med.drug_name,
          detail: buildChangedDetail(
            prev_qty,
            curr_qty,
            prev_usage,
            curr_usage
          ),
        })
        continue
      }
      rows.push({
        key: `cont-${med.key}`,
        kind: 'continued',
        drug_name: med.drug_name,
        detail: buildAddedDetail(curr_qty, curr_usage),
      })
    }
    for (const med of prevMeds) {
      if (currMeds.some((c) => sameDrug(c, med))) continue
      rows.push({
        key: `stop-${med.key}`,
        kind: 'stopped',
        drug_name: med.drug_name,
        // 中止行は前回値を表示 + 行全体に line-through で「もう使わない」 を明示
        detail: buildAddedDetail(
          formatQuantity(med.quantity, med.unit),
          formatUsageWithDays(med.usage, med.days)
        ),
      })
    }
  }

  rows.sort((a, b) => KIND_ORDER[a.kind] - KIND_ORDER[b.kind])

  return (
    <Box sx={GUIDANCE_PREP_GRID_MARKER_SX}>
      {rows.map((row) => {
        // 中止行は薬剤名 + 詳細 に line-through で「もう使わない」 を明示.
        // 印自体は line-through せず文言が読めるようにする.
        const stopped = row.kind === 'stopped'
        const lineThrough = stopped ? 'line-through' : 'none'
        return (
          <Fragment key={row.key}>
            <Typography
              variant="caption"
              component="span"
              sx={{
                ...SUPPLEMENT_SX,
                textAlign: 'center',
                userSelect: 'none',
              }}
            >
              {KIND_SHORT[row.kind]}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: colorTokens.fg.default,
                textDecoration: lineThrough,
              }}
            >
              {row.drug_name}
            </Typography>
            <Typography
              variant="caption"
              component="span"
              sx={{
                ...SUPPLEMENT_SX,
                whiteSpace: 'pre-line',
                textDecoration: lineThrough,
              }}
            >
              {row.detail}
            </Typography>
          </Fragment>
        )
      })}
    </Box>
  )
}

// 加算 panel の col 1 (= 加算名) width. 「乳幼児服薬指導加算」「服用薬剤調整支援料」
// (= 9 char) を 12px (補足の段) で fit. ヒアリング label「症状」「副作用」 にも共用.
export const GUIDANCE_PREP_LABEL_COL_WIDTH = '120px'

// 共通 grid base. gridTemplateColumns は各 panel で個別に指定 (= 2 col か 3 col かが panel ごと違うため).
// 共通: columnGap / rowGap / alignItems で「行間隔 + ベースライン整列」 を統一.
export const GUIDANCE_PREP_GRID_BASE_SX = {
  display: 'grid',
  columnGap: 1.5,
  rowGap: 0.25,
  alignItems: 'baseline' as const,
}

// 2 col (= 前回ヒアリング 用. col 1 = label 110px / col 2 = 値 1fr).
export const GUIDANCE_PREP_GRID_2COL_SX = {
  ...GUIDANCE_PREP_GRID_BASE_SX,
  gridTemplateColumns: `${GUIDANCE_PREP_LABEL_COL_WIDTH} 1fr`,
}

// 3 col 「marker + label + value」 共通 grid.
// 処方 (= 新/変/中 + 薬剤名 + 詳細) と 加算 (= ○/× + 加算名 + reason) で 同形使用.
// col 1 = 1-char marker (20px) / col 2 = label (max-content) / col 3 = value (1fr).
// section 間の行頭 marker 縦位置を揃えて視覚統一感を担保.
export const GUIDANCE_PREP_GRID_MARKER_SX = {
  ...GUIDANCE_PREP_GRID_BASE_SX,
  gridTemplateColumns: `20px max-content 1fr`,
}

// 共通 label cell typography. 補足の段 (12/400/fg.muted) に揃える (hub design-book ADR-0061).
export const GuidancePrepLabel = ({
  children,
}: {
  children: React.ReactNode
}) => (
  <Typography
    variant="caption"
    sx={{
      ...SUPPLEMENT_SX,
      userSelect: 'none',
      whiteSpace: 'nowrap',
    }}
  >
    {children}
  </Typography>
)
